import { Injectable } from '@angular/core';
import { getEnumValues } from '@core/helpers';
import {
  doctorSpecializationRegistry,
  EDoctorSpecializationDTO,
  getDoctorSpecializationEnumKey,
  getDoctorSpecializationLocalization,
  getSelectableDoctorSpecializationCodes,
} from '@project/view-models';
import { ISelectOption } from '@lib/ui/form-elements';

@Injectable({
  providedIn: 'root',
})
export class DoctorSpecializationDataProvider {
  // TODO: get from backend.
  private specializations = [
    { id: 0, code: EDoctorSpecializationDTO.Infectologist },
    { id: 1, code: EDoctorSpecializationDTO.GeneralPhysician },
    { id: 2, code: EDoctorSpecializationDTO.Pulmonologist },
    { id: 3, code: EDoctorSpecializationDTO.Cardiologist },
    { id: 4, code: EDoctorSpecializationDTO.Intensivist },
    { id: 5, code: EDoctorSpecializationDTO.Dermatologist },
    { id: 6, code: EDoctorSpecializationDTO.Physiotherapist },
    { id: 7, code: EDoctorSpecializationDTO.Psychologist },
    { id: 8, code: EDoctorSpecializationDTO.Psychiatrist },
    { id: 9, code: EDoctorSpecializationDTO.VascularSurgeon },
    { id: 10, code: EDoctorSpecializationDTO.Endocrinologist },
    { id: 11, code: EDoctorSpecializationDTO.Gastroenterologist },
    { id: 12, code: EDoctorSpecializationDTO.Gynecologist },
    { id: 13, code: EDoctorSpecializationDTO.Obstetric },
    { id: 14, code: EDoctorSpecializationDTO.Hematologist },
    { id: 15, code: EDoctorSpecializationDTO.Nephrologist },
    { id: 16, code: EDoctorSpecializationDTO.Neurologist },
    { id: 17, code: EDoctorSpecializationDTO.Nutritionist },
    { id: 18, code: EDoctorSpecializationDTO.Ophthalmologist },
    { id: 19, code: EDoctorSpecializationDTO.Orthopedist },
    { id: 20, code: EDoctorSpecializationDTO.Otolaryngologist },
    { id: 21, code: EDoctorSpecializationDTO.Pediatrician },
    { id: 22, code: EDoctorSpecializationDTO.ChildPsychiatrist },
    { id: 23, code: EDoctorSpecializationDTO.Rheumatologist },
    { id: 24, code: EDoctorSpecializationDTO.Traumatologist },
    { id: 25, code: EDoctorSpecializationDTO.Urologist },
    { id: 26, code: EDoctorSpecializationDTO.GeneralSurgeon },
    { id: 27, code: EDoctorSpecializationDTO.ChildNeurologist },
    { id: 28, code: EDoctorSpecializationDTO.ChildPsychologist },
    { id: 29, code: EDoctorSpecializationDTO.Geriatrician },
    { id: 30, code: EDoctorSpecializationDTO.Hepatology },
    { id: 31, code: EDoctorSpecializationDTO.Allergology },
    { id: 32, code: EDoctorSpecializationDTO.ChildEndocrinologist },
    { id: 33, code: EDoctorSpecializationDTO.ChildCardiologist },
  ];

  // It was request to temporally reduce the list of specializations
  private short_specializations = [
    { id: 1, code: EDoctorSpecializationDTO.GeneralPhysician },
    { id: 5, code: EDoctorSpecializationDTO.Dermatologist },
    { id: 12, code: EDoctorSpecializationDTO.Gynecologist },
    { id: 8, code: EDoctorSpecializationDTO.Psychiatrist },
    { id: 7, code: EDoctorSpecializationDTO.Psychologist },
    { id: 16, code: EDoctorSpecializationDTO.Neurologist },
    { id: 25, code: EDoctorSpecializationDTO.Urologist },
    { id: 3, code: EDoctorSpecializationDTO.Cardiologist },
    { id: 10, code: EDoctorSpecializationDTO.Endocrinologist },
    { id: 21, code: EDoctorSpecializationDTO.Pediatrician },
    { id: 19, code: EDoctorSpecializationDTO.Orthopedist },
    { id: 17, code: EDoctorSpecializationDTO.Nutritionist },
    { id: 27, code: EDoctorSpecializationDTO.ChildNeurologist },
    { id: 28, code: EDoctorSpecializationDTO.ChildPsychologist },
  ];

  getSelectOptions(isShortSpecialization: boolean = false) {
    return this.buildOptions(isShortSpecialization, (specialization) => specialization.id.toString());
  }

  // Options whose value is the EDoctorSpecializationDTO code instead of the numeric id.
  // Needed wherever the selected value is persisted/compared as EDoctorSpecializationDTO
  // (e.g. doctor invite/edit form), since the backend doctor specialization field expects the code.
  // `currentCode` keeps the doctor's present specialization selectable in the edit form even when it was
  // deactivated (or is missing from the list), so the select doesn't render blank.
  getSelectOptionsByCode(isShortSpecialization: boolean = false, currentCode?: string) {
    return this.buildOptions(isShortSpecialization, (specialization) => specialization.code, currentCode);
  }

  // Options whose value is the EDoctorSpecializationDTO member name (e.g. 'Geriatrician' for the code
  // 'Geriatrics'), which is what the receptionist "send to live queue" action posts to the backend.
  // A code that is not in the enum (configured only in Firestore) is sent as is.
  getSelectOptionsByEnumKey() {
    const localeKey = doctorSpecializationRegistry.getLocaleKey();

    return getSelectableDoctorSpecializationCodes()
      .map((code) => ({
        value: getDoctorSpecializationEnumKey(code),
        label: getDoctorSpecializationLocalization(code),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, localeKey));
  }

  // Selectable list: the remote (Firestore/cache) active specializations, or the hardcoded lists above
  // when no remote list is available. Deactivated specializations are never offered for selection.
  private getSelectableSpecializations(isShortSpecialization: boolean): { id: number; code: string }[] {
    if (isShortSpecialization) {
      return this.short_specializations.filter(
        (specialization) => !doctorSpecializationRegistry.isDeactivated(specialization.code),
      );
    }

    return doctorSpecializationRegistry.getActiveEntries() ?? this.specializations;
  }

  private buildOptions(
    isShortSpecialization: boolean,
    getValue: (specialization: { id: number; code: string }) => string,
    currentCode?: string,
  ) {
    const localeKey = doctorSpecializationRegistry.getLocaleKey();

    const options = this.getSelectableSpecializations(isShortSpecialization).map((specialization) => ({
      value: getValue(specialization),
      label: getDoctorSpecializationLocalization(specialization.code),
    }));

    if (currentCode && !options.some((option) => option.value === currentCode)) {
      options.push({ value: currentCode, label: getDoctorSpecializationLocalization(currentCode) });
    }

    options.sort((a, b) => a.label.localeCompare(b.label, localeKey));

    return options;
  }
}
